import {
  BUCKET_ROLE_CACHE,
  BUCKET_ROLE_FILE_PICKUP,
  BUCKET_ROLE_HOME,
} from './bucket-roles';
import { isSourceLocationPathFormatValid } from './workflow-general-info-valid';

/** Tokens plus bare legacy slugs still seen in brownfield sourceLocationPath values. */
const KNOWN_BUCKET_ROLE_SEGMENTS = new Set([
  BUCKET_ROLE_HOME,
  BUCKET_ROLE_CACHE,
  BUCKET_ROLE_FILE_PICKUP,
  'home',
  'cache',
  'file-pickup',
  'filepickup',
]);

/** First path segment is a bucket token or env-prefixed literal bucket name. */
function isSourcePathBucketSegment(segment: string): boolean {
  if (!segment) return false;
  if (KNOWN_BUCKET_ROLE_SEGMENTS.has(segment.toLowerCase())) return true;
  if (/^\{\{.+\}\}$/.test(segment)) return true;
  // ponytail: `efm-{env}-*` literal buckets; extend if non-efm prefixes appear in stored paths.
  if (/^efm-/.test(segment)) return true;
  return false;
}

function toSuffixPath(segments: string[]): string {
  if (segments.length === 0) return '';
  return `/${segments.join('/')}/`;
}

/** Collapse save-stamp artifacts such as repeated BU segments after the bucket token. */
function collapseDuplicateLeadingSegments(segments: string[]): void {
  while (
    segments.length >= 2 &&
    segments[0].toLowerCase() === segments[1].toLowerCase()
  ) {
    segments.splice(1, 1);
  }
}

/**
 * Extract user-editable suffix from a full workflow sourceLocationPath.
 * Strips only the leading bucket token or literal bucket name; keeps the business unit and
 * workflow folders visible in the edit form (e.g. `/{{home}}/JHFT-Testing/MyWorkflow/` →
 * `/JHFT-Testing/MyWorkflow/`).
 */
export function extractSourceLocationSuffix(fullPath: string): string {
  if (fullPath == null || fullPath === '') return '';
  if (typeof fullPath !== 'string') return '';

  const trimmed = fullPath.trim();
  if (!trimmed) return '';

  const normalized = trimmed.endsWith('/') ? trimmed : `${trimmed}/`;
  const segments = normalized.split('/').filter(Boolean);

  if (segments.length === 0) return '';
  if (segments.length === 1) return `/${segments[0]}/`;

  if (isSourcePathBucketSegment(segments[0])) {
    const afterBucket = segments.slice(1);
    collapseDuplicateLeadingSegments(afterBucket);
    return toSuffixPath(afterBucket);
  }

  return toSuffixPath(segments);
}

const HEX32_PATTERN = /^[0-9a-f]{32}$/i;

/** First candidate that is hex32 (any case); empty string if none. */
export function firstHex32WorkflowId(
  ...candidates: Array<string | null | undefined>
): string {
  for (const raw of candidates) {
    const id = String(raw ?? '').trim();
    if (HEX32_PATTERN.test(id)) return id.toLowerCase();
  }
  return '';
}

/** Mirror API `WorkflowIdentityPaths.ResolvePathSegment` — prefer stable id when valid hex32. */
export function resolveWorkflowPathSegment(
  workflowId: string | null | undefined,
  workflowName: string,
): string {
  const id = firstHex32WorkflowId(workflowId);
  if (id) return id;
  return (workflowName ?? '').trim();
}

function stripBraceTokenSegments(path: string): string {
  if (!path) return path;
  const segments = path.split('/').filter(Boolean).filter((seg) => !/^\{\{.+\}\}$/.test(seg));
  if (segments.length === 0) return '';
  return `/${segments.join('/')}/`;
}

export function resolvePushSourceLocationPathForSave(
  formPath: string,
  loadedSuffix: string,
  workflowId: string | null | undefined,
): string {
  const extracted = extractSourceLocationSuffix(formPath ?? '');
  if (isSourceLocationPathFormatValid(extracted)) return extracted;

  const withoutTokens = stripBraceTokenSegments(extracted);
  if (isSourceLocationPathFormatValid(withoutTokens)) return withoutTokens;

  const unchanged =
    String(formPath ?? '').toLowerCase() === String(loadedSuffix ?? '').toLowerCase();
  const hex = firstHex32WorkflowId(workflowId);
  if (unchanged && hex) return `/${hex}/`;

  return extracted || formPath || '';
}

export function resolvePullSourceLocationPathForSave(
  workflowId: string | null | undefined,
  workflowName: string,
): string {
  const fromSegment = `/${resolveWorkflowPathSegment(workflowId, workflowName)}/`;
  if (isSourceLocationPathFormatValid(fromSegment)) return fromSegment;

  const rawId = String(workflowId ?? '').trim();
  const fromId = rawId ? `/${rawId}/` : '';
  if (isSourceLocationPathFormatValid(fromId)) return fromId;

  return fromSegment;
}
